import { FC } from 'react';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
    FormControlLabel,
    Radio,
    RadioGroup,
    Tooltip,
    Typography,
    TypographyProps,
} from '@mui/material';
import { useField } from 'formik';

import { Flex } from 'src/components/Flex';
import { NPICurrencyInputFieldLabelled } from 'src/components/FormikComponents/NPICurrencyInputField/NPICurrencyInputFieldLabelled';

import { TWO_TOGGLE_PRICING_TYPES } from '../TwoToggleRates';

interface CardHeadingProps {
    children: React.ReactNode;
}

const CardHeading: FC<CardHeadingProps> = ({ children }) => {
    return (
        <Typography fontSize="20px" fontWeight={500} lineHeight="30px" color="#686867">
            {children}
        </Typography>
    );
};

interface NoteLabelProps extends TypographyProps {
    children: React.ReactNode;
}

const NoteLabel: FC<NoteLabelProps> = ({ children, ...props }) => {
    return (
        <Typography color="#F6B478" fontSize="14px" lineHeight="21px" fontWeight="400" {...props}>
            {children}
        </Typography>
    );
};

interface NoteTextProps {
    children: React.ReactNode;
}

const NoteText: FC<NoteTextProps> = ({ children }) => {
    return (
        <Typography color="#8E8E8D" fontSize="14px" lineHeight="21px" fontWeight="400">
            {children}
        </Typography>
    );
};

interface RateInputsProps {
    namePrefix: string;
    isFixed: boolean;
    isConstant?: boolean;
}

const RateInputs: FC<RateInputsProps> = ({ namePrefix, isFixed, isConstant }) => {
    return (
        <Flex flexDirection="column" width={isFixed ? 'max-content' : '100%'}>
            {isFixed ? (
                <NPICurrencyInputFieldLabelled
                    fieldName={`${namePrefix}.fixedRate`}
                    header="Fixed Rate"
                    isDisabled={isConstant}
                />
            ) : (
                <Flex width="100%" justifyContent="space-between">
                    <NPICurrencyInputFieldLabelled
                        fieldName={`${namePrefix}.baseRate`}
                        header="Base Rate"
                        isDisabled={isConstant}
                    />
                    <NPICurrencyInputFieldLabelled
                        fieldName={`${namePrefix}.lowestRate`}
                        header="Lowest Rate"
                        isDisabled={isConstant}
                    />
                    <NPICurrencyInputFieldLabelled
                        fieldName={`${namePrefix}.highestRate`}
                        header="Highest Rate"
                        isDisabled={isConstant}
                    />
                </Flex>
            )}
        </Flex>
    );
};

interface ComplianceNoteProps {
    note?: string;
}

const ComplianceNote: FC<ComplianceNoteProps> = ({ note }) => {
    if (!note) return null;

    return (
        <Flex maxWidth="90%">
            <NoteLabel marginRight="8px">Note:</NoteLabel>
            <NoteText>{note}</NoteText>
        </Flex>
    );
};

interface ComplianceCardProps {
    isOdd?: boolean;
    namePrefix: string;
    heading: string;
    isConstant?: boolean;
    note?: string;
    tooltip?: string;
}

export const ComplianceCard: FC<ComplianceCardProps> = ({
    isOdd,
    namePrefix,
    heading,
    isConstant,
    note,
    tooltip,
}) => {
    const [{ value }, _, { setValue }] = useField(`${namePrefix}.type`);
    const isFixed = value === 'FIXED';

    return (
        <Flex
            padding="16px 8px"
            bgcolor={isOdd ? 'rgba(250, 218, 187, 0.15)' : 'transparent'}
            flexDirection="column"
            gap="24px"
        >
            <Flex justifyContent="space-between">
                <Flex>
                    <CardHeading>{heading}</CardHeading>
                    {tooltip && (
                        <Flex sx={{ cursor: 'help' }}>
                            <Tooltip title={tooltip} placement="bottom">
                                <HelpOutlineIcon sx={{ color: '#FDC800', marginLeft: '18px' }} />
                            </Tooltip>
                        </Flex>
                    )}
                </Flex>
                <RadioGroup
                    row
                    onChange={(e) => {
                        setValue(e.target.value);
                    }}
                >
                    {TWO_TOGGLE_PRICING_TYPES.map(({ label, value: optionValue }) => {
                        return (
                            <FormControlLabel
                                key={optionValue}
                                value={optionValue}
                                control={<Radio />}
                                checked={value === optionValue}
                                label={label}
                                disabled={isConstant}
                            />
                        );
                    })}
                </RadioGroup>
            </Flex>

            <ComplianceNote note={note} />
            <RateInputs namePrefix={namePrefix} isFixed={isFixed} isConstant={isConstant} />
        </Flex>
    );
};
